import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import {
	LoAvatar,
	LoSkeleton,
	LoStatus,
	LoToastService
} from 'lo-design-system';
import {
	AdAccountListItem,
	PartnershipPair
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import { MetaInstagramAccountsService } from '@brand-lolly/app/services/meta-integrations/instagram-accounts.service';
import { extractMetaErrorMessage } from '@brand-lolly/app/shared/utils/meta-api-error.utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface PairRow {
	pair: PartnershipPair;
	adAccount: AdAccountListItem | null;
}

@Component({
	standalone: true,
	selector: 'meta-partnership-pair-list',
	imports: [LoAvatar, LoSkeleton, LoStatus],
	template: `
		@if (loading) {
		<div class="skeletons" aria-busy="true">
			@for (n of skeletonIds; track n) {
			<lo-skeleton variant="row"></lo-skeleton>
			}
		</div>
		} @else if (rows.length === 0) {
		<p class="empty lo-type-text-md">
			No ad accounts linked via Partnership Ads.
		</p>
		} @else {
		<div class="pair-list">
			@for (row of rows; track row.pair.ad_account_id) {
			<div class="pair-row">
				<lo-avatar
					[name]="row.adAccount?.name || row.pair.ad_account_id"
					[src]="row.adAccount?.business_picture_url || ''"
				></lo-avatar>
				<div class="pair-info">
					<span class="lo-type-text-sm-semibold">
						{{ row.adAccount?.name || row.pair.ad_account_id }}
					</span>
					<span class="pair-meta lo-type-text-xs">
						{{ row.adAccount?.currency || '—' }} ·
						{{ campaignsLabel(row.adAccount) }}
					</span>
				</div>
				@if (row.pair.is_enabled) {
				<lo-status tone="success">Enabled</lo-status>
				}
			</div>
			}
		</div>
		}
	`,
	styles: [
		`
			:host {
				display: block;

				.skeletons,
				.pair-list {
					display: flex;
					flex-direction: column;
					gap: var(--lo-space-gap-md);
				}

				.empty {
					margin: 0;
					color: var(--lo-color-text-secondary);
				}

				.pair-row {
					display: flex;
					align-items: center;
					gap: var(--lo-space-gap-md);

					.pair-info {
						display: flex;
						flex: 1;
						flex-direction: column;
						min-width: 0;

						.pair-meta {
							color: var(--lo-color-text-secondary);
						}
					}
				}
			}
		`
	]
})
export class MetaPartnershipPairListComponent implements OnInit, OnDestroy {
	@Input({ required: true }) accountId!: number;
	@Input() adAccounts: AdAccountListItem[] = [];

	@Output() countsLoaded = new EventEmitter<{
		enabled: number;
		total: number;
	}>();

	loading = true;
	rows: PairRow[] = [];
	skeletonIds = [0, 1, 2];

	private destroy$ = new Subject<void>();

	constructor(
		private service: MetaInstagramAccountsService,
		private toasts: LoToastService
	) {}

	ngOnInit(): void {
		this.service
			.getPartnershipPairs(this.accountId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: pairs => {
					const enabledPairs = pairs.filter(p => p.is_enabled);
					this.rows = enabledPairs.map(pair => ({
						pair,
						adAccount:
							this.adAccounts.find(
								a => a.id === pair.ad_account_id
							) ?? null
					}));
					this.loading = false;
					this.countsLoaded.emit({
						enabled: enabledPairs.length,
						total: pairs.length
					});
				},
				error: error => {
					this.loading = false;
					this.toasts.error(
						extractMetaErrorMessage(
							error,
							'Failed to load partnership pairs'
						)
					);
				}
			});
	}

	campaignsLabel(ad: AdAccountListItem | null): string {
		const count = ad?.campaigns_count ?? 0;
		if (count === 0) return 'no campaigns';
		return `${count} campaign${count === 1 ? '' : 's'}`;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
